if (!customElements.get("byte-collection-list")) {
  const DRAG_THRESHOLD = 5;

  class ByteCollectionList extends HTMLElement {
    constructor() {
      super();

      this.isReady = false;
      this.isPointerDown = false;
      this.isDragging = false;
      this.didDrag = false;
      this.dragStartX = 0;
      this.dragStartScrollLeft = 0;
      this.activePointerId = null;

      this.boundHandlePrevClick = this.handlePrevClick.bind(this);
      this.boundHandleNextClick = this.handleNextClick.bind(this);
      this.boundHandleScroll = this.handleScroll.bind(this);
      this.boundHandleResize = this.handleResize.bind(this);
      this.boundHandlePointerDown = this.handlePointerDown.bind(this);
      this.boundHandlePointerMove = this.handlePointerMove.bind(this);
      this.boundHandlePointerEnd = this.handlePointerEnd.bind(this);
      this.boundHandleClickCapture = this.handleClickCapture.bind(this);
      this.boundHandleDragStart = this.handleDragStart.bind(this);
    }

    connectedCallback() {
      this.#cacheElements();

      if (!this.viewport) return;
      if (!this.viewport.firstElementChild) return;

      this.isReady = true;

      if (this.navigation) this.navigation.removeAttribute("aria-hidden");

      this.#bindEvents();
      this.#updateState();
    }

    disconnectedCallback() {
      this.#unbindEvents();
      this.isReady = false;
    }

    handlePrevClick() {
      this.#scrollByDirection(-1);
    }

    handleNextClick() {
      this.#scrollByDirection(1);
    }

    handleScroll() {
      this.#updateState();
    }

    handleResize() {
      this.#updateState();
    }

    handlePointerDown(event) {
      // Touch and pen keep native scrolling
      if (event.pointerType !== "mouse" || event.button !== 0) return;

      this.isPointerDown = true;
      this.isDragging = false;
      this.didDrag = false;
      this.dragStartX = event.clientX;
      this.dragStartScrollLeft = this.viewport.scrollLeft;
      this.activePointerId = event.pointerId;
    }

    handlePointerMove(event) {
      if (!this.isPointerDown) return;
      if (event.pointerId !== this.activePointerId) return;

      const deltaX = event.clientX - this.dragStartX;

      if (!this.isDragging) {
        if (Math.abs(deltaX) < DRAG_THRESHOLD) return;

        this.#startDragging();
      }

      this.viewport.scrollLeft = this.dragStartScrollLeft - deltaX;
    }

    handlePointerEnd(event) {
      if (!this.isPointerDown) return;
      if (event.pointerId !== this.activePointerId) return;

      this.isPointerDown = false;

      if (this.isDragging) this.#stopDragging(event.pointerId);

      this.activePointerId = null;
    }

    handleClickCapture(event) {
      if (!this.didDrag) return;

      // Prevent accidental card navigation right after a drag
      event.preventDefault();
      event.stopPropagation();
      this.didDrag = false;
    }

    handleDragStart(event) {
      // Block native image/link ghost dragging
      event.preventDefault();
    }

    #cacheElements() {
      this.viewport = this.querySelector("[data-cl-viewport]");
      this.previousButton = this.querySelector("[data-cl-prev]");
      this.nextButton = this.querySelector("[data-cl-next]");
      this.progressFill = this.querySelector("[data-cl-fill]");
      this.navigation = this.querySelector("[data-cl-nav]");
    }

    #bindEvents() {
      this.previousButton?.addEventListener("click", this.boundHandlePrevClick);
      this.nextButton?.addEventListener("click", this.boundHandleNextClick);

      this.viewport.addEventListener("scroll", this.boundHandleScroll, {
        passive: true,
      });
      this.viewport.addEventListener(
        "pointerdown",
        this.boundHandlePointerDown,
      );
      this.viewport.addEventListener(
        "pointermove",
        this.boundHandlePointerMove,
      );
      this.viewport.addEventListener("pointerup", this.boundHandlePointerEnd);
      this.viewport.addEventListener(
        "pointercancel",
        this.boundHandlePointerEnd,
      );
      this.viewport.addEventListener(
        "click",
        this.boundHandleClickCapture,
        true,
      );
      this.viewport.addEventListener("dragstart", this.boundHandleDragStart);

      window.addEventListener("resize", this.boundHandleResize, {
        passive: true,
      });
    }

    #unbindEvents() {
      this.previousButton?.removeEventListener(
        "click",
        this.boundHandlePrevClick,
      );
      this.nextButton?.removeEventListener("click", this.boundHandleNextClick);

      this.viewport?.removeEventListener("scroll", this.boundHandleScroll);
      this.viewport?.removeEventListener(
        "pointerdown",
        this.boundHandlePointerDown,
      );
      this.viewport?.removeEventListener(
        "pointermove",
        this.boundHandlePointerMove,
      );
      this.viewport?.removeEventListener(
        "pointerup",
        this.boundHandlePointerEnd,
      );
      this.viewport?.removeEventListener(
        "pointercancel",
        this.boundHandlePointerEnd,
      );
      this.viewport?.removeEventListener(
        "click",
        this.boundHandleClickCapture,
        true,
      );
      this.viewport?.removeEventListener(
        "dragstart",
        this.boundHandleDragStart,
      );

      window.removeEventListener("resize", this.boundHandleResize);
    }

    #updateState() {
      if (!this.isReady) return;

      const scrollLeft = this.viewport.scrollLeft;
      const maxScroll = this.viewport.scrollWidth - this.viewport.clientWidth;

      if (this.previousButton) this.previousButton.disabled = scrollLeft <= 2;
      if (this.nextButton)
        this.nextButton.disabled = scrollLeft >= maxScroll - 2;

      if (!this.progressFill) return;

      if (maxScroll > 0) {
        const scrollPercent = Math.min(
          100,
          Math.round((scrollLeft / maxScroll) * 100),
        );
        const visibleFraction =
          this.viewport.clientWidth / this.viewport.scrollWidth;
        const fillPercent = Math.round(
          visibleFraction * 100 + scrollPercent * (1 - visibleFraction),
        );

        this.progressFill.style.width = `${fillPercent}%`;
      } else {
        this.progressFill.style.width = "100%";
      }
    }

    #scrollByDirection(direction) {
      if (!this.isReady) return;

      const firstSlide = this.viewport.firstElementChild;
      const slideWidth = firstSlide ? firstSlide.offsetWidth : 0;
      const gap = parseInt(getComputedStyle(this.viewport).gap, 10) || 16;
      const step = slideWidth + gap;
      const maxScroll = this.viewport.scrollWidth - this.viewport.clientWidth;
      let targetScroll = this.viewport.scrollLeft + direction * step;

      if (direction > 0 && maxScroll - targetScroll < step)
        targetScroll = maxScroll;
      targetScroll = Math.max(0, Math.min(maxScroll, targetScroll));

      this.viewport.scrollTo({ left: targetScroll, behavior: "smooth" });
    }

    #startDragging() {
      this.isDragging = true;
      this.didDrag = true;
      this.viewport.classList.add("is-dragging");
      this.viewport.style.scrollSnapType = "none";
      this.viewport.setPointerCapture?.(this.activePointerId);
    }

    #stopDragging(pointerId) {
      this.isDragging = false;
      this.viewport.classList.remove("is-dragging");
      this.viewport.style.scrollSnapType = "";

      if (this.viewport.hasPointerCapture?.(pointerId)) {
        this.viewport.releasePointerCapture(pointerId);
      }
    }
  }

  customElements.define("byte-collection-list", ByteCollectionList);
}
